import * as Api from '@ViewModels';
// Imported directly (not via './index') to avoid pulling the whole utils barrel into this module.
import { convertHtmlStringValueToPlainText, truncateTextToLength } from './stringUtils';

/**
 * Detects copy the content team expects a client to replace before a campaign goes out.
 *
 * Nothing on ITemplate/IBaseTemplate records "this template must be edited", so detection works off
 * markers left in the copy itself.
 */

type CustomizationMarkerLocation = 'subject' | 'body';

type CustomizationMarkerKind = 'placeholder' | 'notice';

export interface ICustomizationMarker {
	/** Where in the campaign the marker was found. */
	location: CustomizationMarkerLocation;
	/**
	 * 'placeholder' is the current [[ ]] convention. 'notice' is the older "THIS TEMPLATE MUST BE
	 * CUSTOMIZED" banner the content team used before [[ ]] existed.
	 */
	kind: CustomizationMarkerKind;
	/** Display text shown to the user so they can scan the email for it. */
	text: string;
}

/**
 * The convention content authors use, e.g. "[[Insert your office address]]". The inner class excludes
 * brackets so a stray "[[" cannot pair with a "]]" elsewhere in the block.
 */
const PlaceholderMarkerRegex = /\[\[([^[\]]{1,120})\]\]/g;

/**
 * The convention that predates [[ ]], e.g.
 * "*NOTE: THIS TEMPLATE MUST BE CUSTOMIZED (DELETE THIS NOTICE BEFORE SENDING)*". Matching the
 * asterisk form separately lets us display just the notice instead of its whole paragraph.
 */
const LegacyAsteriskNoticeRegex = /\*([^*]{0,200}?\btemplate\b[^*]{0,200}?customiz[^*]{0,200}?)\*/i;

/** Fallback for notices styled with markup rather than asterisks, e.g. bolded red text. */
const LegacyNoticeCoOccurrenceRegexes = [
	/\btemplates?\b[^.!?]{0,60}?customiz/i,
	/customiz[^.!?]{0,60}?\btemplates?\b/i,
];

/**
 * Co-occurrence alone is too loose: ordinary copy can mention customizing a template ("our templates are
 * fully customizable"). A notice aimed at the client also instructs them, so require that too.
 */
const NoticeIntentRegex =
	/\b(?:must|should|needs? to|has to|have to|please|be sure|remember to|don'?t forget)\b|\b(?:requires?|needs) customiz|\bcustomiz\w* (?:is )?required\b|\bdelete this\b|\b(?:before|prior to) (?:sending|posting|publishing)\b/i;

/** An all-caps line mentioning both is a notice, not prose, even without instructing language. */
const isShoutedNotice = (block: string) => /[A-Z]/.test(block) && block === block.toUpperCase();

const BlockLevelTagRegex =
	/<\s*\/?\s*(?:p|div|br|tr|td|th|li|ul|ol|h[1-6]|table|tbody|thead|blockquote|section|article|header|footer|pre|hr)\b[^>]*>/gi;

const MaxNoticeDisplayLength = 140;

/**
 * Markers routinely straddle inline markup ("[[<strong>Insert name</strong>]]"), so matching raw HTML
 * misses them; flattening to one string instead runs paragraphs together, because textContent joins
 * blocks with no separator. Restoring block boundaries first avoids both.
 */
const getVisibleTextBlocks = (html?: string): string[] => {
	if (!html) {
		return [];
	}
	const withLineBreaks = html.replace(BlockLevelTagRegex, '\n');
	return convertHtmlStringValueToPlainText(withLineBreaks)
		.split('\n')
		.map(x => x.replace(/\s+/g, ' ').trim())
		.filter(x => !!x);
};

/**
 * Unlayer newsletters store the rendered page directly in `document` with no <lev-content> wrapper, and
 * the Api helper returns '' for anything unwrapped, so fall back to the raw document.
 */
const getContentHtml = (content?: Api.IRawRichTextContentState) => {
	if (!content?.document) {
		return '';
	}
	return Api.getContentHtmlStringValueFromRawRichTextContentState(content) || content.document;
};

const getNoticeDisplayText = (block: string) => {
	const asteriskMatch = LegacyAsteriskNoticeRegex.exec(block);
	const notice = asteriskMatch ? asteriskMatch[1].trim() : block;
	return notice.length > MaxNoticeDisplayLength ? `${truncateTextToLength(notice, MaxNoticeDisplayLength)}…` : notice;
};

const findMarkersInBlocks = (blocks: string[], location: CustomizationMarkerLocation) => {
	const markers: ICustomizationMarker[] = [];
	blocks.forEach(block => {
		PlaceholderMarkerRegex.lastIndex = 0;
		for (;;) {
			const match = PlaceholderMarkerRegex.exec(block);
			if (!match) {
				break;
			}
			if (match[1].trim()) {
				markers.push({ kind: 'placeholder', location, text: match[0].replace(/\s+/g, ' ') });
			}
		}

		const isLegacyNotice =
			LegacyAsteriskNoticeRegex.test(block) ||
			(LegacyNoticeCoOccurrenceRegexes.some(regex => regex.test(block)) &&
				(NoticeIntentRegex.test(block) || isShoutedNotice(block)));
		if (isLegacyNotice) {
			markers.push({ kind: 'notice', location, text: getNoticeDisplayText(block) });
		}
	});
	return markers;
};

const dedupeMarkers = (markers: ICustomizationMarker[]) => {
	const seen = new Set<string>();
	return markers.filter(marker => {
		const key = `${marker.location}:${marker.kind}:${marker.text.toLowerCase()}`;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

const toPlainTextBlocks = (text?: string) =>
	(text ?? '')
		.split(/\r?\n/)
		.map(x => x.replace(/\s+/g, ' ').trim())
		.filter(x => !!x);

/**
 * @returns markers found in the subject and body, subject first, deduplicated. Empty when the content
 * looks ready to send.
 */
export const findCustomizationMarkers = (
	content?: Api.IRawRichTextContentState,
	subject?: string
): ICustomizationMarker[] => {
	// The subject skips the markup flattening, which would eat characters like "<3"
	const subjectMarkers = findMarkersInBlocks(toPlainTextBlocks(subject), 'subject');
	const bodyMarkers = findMarkersInBlocks(getVisibleTextBlocks(getContentHtml(content)), 'body');
	return dedupeMarkers([...subjectMarkers, ...bodyMarkers]);
};

/** Social posts hold their copy as a plain string rather than a rich content document. */
export const findCustomizationMarkersInPlainText = (text?: string): ICustomizationMarker[] =>
	dedupeMarkers(findMarkersInBlocks(toPlainTextBlocks(text), 'body'));
